import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { lastValueFrom, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { IDocumentResponse, IDocumentType } from '../models/document';
import { KeepOriginalHeadersKey } from '../shared/security/security.interceptor';
import { ConfigurationService, ForceBaseUrl } from './configuration/configuration.service';
import { EndpointFactory } from './endpoint-factory.service';

@Injectable({
    providedIn: 'root'
})
export class DocumentEndpoint extends EndpointFactory {

    getServiceBaseUrl(force?: ForceBaseUrl) {
        return this.getBaseUrl('document', force) + 'document';
    }

    constructor(http: HttpClient, configurations: ConfigurationService, injector: Injector) {

        super(http, configurations, injector);
    }

    uploadDocEndpoint<T>(tenantId: string, memberId: string, input: FormData): Observable<T> {
        const headers = new HttpHeaders().set(KeepOriginalHeadersKey, 'keep');
        const endpointUrl = `${this.getServiceBaseUrl()}/${tenantId}/${memberId}/upload`;
        return this.http.post<T>(endpointUrl, input, { headers: headers }).pipe<T>(
            catchError(error => {
                return this.handleError(error);
            }));
    }

    uploadDocsEndpoint<T>(tenantId: string, memberId: string, input: FormData[]): Observable<T> {
        const headers = new HttpHeaders().set('content-type', 'multipart/form-data').set(KeepOriginalHeadersKey, 'keep');
        const endpointUrl = `${this.getServiceBaseUrl()}/${tenantId}/${memberId}/upload`;
        return this.http.post<T>(endpointUrl, input, { headers: headers }).pipe<T>(
            catchError(error => {
                return this.handleError(error);
            }));
    }

    getDocumentTypeEndpoint(): Observable<IDocumentType[]> {
        const endpointUrl = `${this.getServiceBaseUrl()}/getdocumenttype`;
        return this.http.get<IDocumentType[]>(endpointUrl).pipe(
            catchError(error => {
                return this.handleError(error);
            }));
    }

    getFiles<T>(tenantId: string, memberId: string, documentTypeMetadataFilter: string = null): Observable<T> {
        let endpointUrl = `${this.getServiceBaseUrl()}/${tenantId}/${memberId}/getfiles`;

        if (documentTypeMetadataFilter != null) {
            endpointUrl += `?documentTypeMetadataFilter=${documentTypeMetadataFilter}`;
        }
        return this.http.get<T>(endpointUrl).pipe<T>(
            catchError(error => {
                return this.handleError(error);
            }));
    }

    getDocument<T>(tenantId: string, documentId: number): Observable<T> {
        var endpointUrl = `${this.getServiceBaseUrl()}/${tenantId}/${documentId}`;

        return this.http.get<T>(endpointUrl).pipe<T>(
            catchError(error => {
                return this.handleError(error);
            }));
    }

    updateDocEndpoint<T>(tenantId: string, file: Document): Observable<T> {
        console.log('Doc Endpoint service', tenantId, file)
        const endpointUrl = `${this.getServiceBaseUrl()}/${tenantId}/update`;
        return this.http.put<T>(endpointUrl, file).pipe<T>(
            catchError(error => {
                return this.handleError(error);
            }));
    }

    deleteDocEndpoint<T>(tenantId: string, docId: string): Observable<T> {
        console.log('Doc Endpoint service', tenantId, docId)
        const endpointUrl = `${this.getServiceBaseUrl()}/${tenantId}/${docId}/delete`;
        return this.http.delete<T>(endpointUrl).pipe<T>(
            catchError(error => {
                return this.handleError(error);
            }));
    }

    downloadDocEndpoint<T>(tenantId: string, memberId: string, docId: string): Observable<any> {
        const headers = new HttpHeaders().set(KeepOriginalHeadersKey, 'keep');
        const endpointUrl = `${this.getServiceBaseUrl()}/${tenantId}/${memberId}/${docId}/download`;

        return this.http.post(endpointUrl, docId, { headers: headers, responseType: 'blob' }).pipe(
            catchError(error => {
                return this.handleError(error);
            }));
    }

    async latestDocumentByType(tenantId: string, memberId: string, type: string, download?: boolean): Promise<IDocumentResponse> {
        let endpointUrl = `${this.getServiceBaseUrl()}/${tenantId}/${memberId}/${type}`;
        endpointUrl = download ? endpointUrl + `?download=${download}` : endpointUrl;

        return lastValueFrom(this.http.get<IDocumentResponse>(endpointUrl).pipe(
            catchError(error => {
                return this.handleError(error);
            })));
    }

    async documentsByType(tenantId: string, memberId: string, type: string): Promise<IDocumentResponse[]> {
        let endpointUrl = `${this.configurations.getBaseUrl(undefined, 'WEBAPI')}documents/${tenantId}/${memberId}/${type}`;

        return lastValueFrom(this.http.get<IDocumentResponse[]>(endpointUrl).pipe(
            catchError(error => {
                return this.handleError(error);
            })));
    }
}
